import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Button, IconArrowLeft, IconArrowRight, InformationalScreen, useDirection, } from '@adc/parallax-component-library';
import { ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAccount, useApiError, useAuth } from 'Hooks';
import { Banner, FormContainer, InputPassword, MainContainer, PasswordInputValidator, } from 'Components/utility';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { updatePassword } from 'Services/patient';
const mapStateToProps = ({ config: { passwordRules }, auth, env: { deviceId, osType, osVersion, app, appVersion }, }) => {
    return {
        email: auth.include.patient.email,
        accountId: auth.account_id,
        deviceId,
        osType,
        osVersion,
        app,
        appVersion,
        country: auth.include.patient.country,
        passwordRules,
    };
};
const initialValuesFormik = {
    currentPassword: '',
    newPassword: '',
    repeatNewPassword: '',
};
const connector = connect(mapStateToProps);
const UpdatePassword = ({ email, accountId, osType, osVersion, app, appVersion, country, passwordRules, }) => {
    const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);
    const [formRefreshFlag, setFormRefreshFlag] = useState(false);
    const handleClickBack = useCallback(() => {
        mediator.publish('router:back');
    }, []);
    const isLtr = useDirection() === 'ltr';
    const { showPasswordValidationError } = useAccount();
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const { handleLoginError, handleLockoutError, showHcpError, isLocked, lockoutMessage, lockoutSubmessage, } = useAuth('changepassword');
    const validationSchema = Yup.object().shape({
        currentPassword: Yup.string()
            .required(i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.errors.required'))
            .trim(),
        repeatNewPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.errors.passwordMustMatch'))
            .required(i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.errors.required')),
    });
    const handleClickForgotPass = () => {
        mediator.publish('router:navigate', '/forgot-password');
    };
    const { handleSubmit, handleBlur, handleChange, setFieldValue, setTouched, isValid, isSubmitting, errors, touched, values, } = useFormik({
        initialValues: initialValuesFormik,
        onSubmit: onSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
    });
    const handleApiRequestError = useCallback((error, resetForm) => {
        switch (error.status) {
            case ApiStatus.FORBIDDEN: {
                const code = error.response?.data.code;
                if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
                    showHcpError();
                }
                break;
            }
            case ApiStatus.UNAUTHORIZED:
                handleLoginError(error, () => {
                    resetForm();
                    setFormRefreshFlag(!formRefreshFlag);
                }, () => setFieldValue('currentPassword', ''));
                break;
            case ApiStatus.BAD_REQUEST: {
                setFieldValue('newPassword', '');
                setFieldValue('repeatNewPassword', '');
                setTouched({});
                setFormRefreshFlag(!formRefreshFlag);
                const errors = error.response?.data.errors;
                const code = error.response?.data.code;
                if (code === ApiStatus.PASSWORD_VALIDATION) {
                    showPasswordValidationError(passwordRules, errors, country);
                    return;
                }
                showApiErrorModal();
                break;
            }
            case ApiStatus.TOO_MANY_REQUESTS: {
                handleLockoutError(error);
                resetForm();
                break;
            }
            default:
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
        }
    }, [
        handleLoginError,
        showNetworkErrorModal,
        showApiErrorModal,
        showHcpError,
        formRefreshFlag,
        setFieldValue,
        setTouched,
        showPasswordValidationError,
        passwordRules,
        country,
        handleLockoutError,
    ]);
    function onSubmitForm(data, { setSubmitting, resetForm }) {
        updatePassword({
            userID: accountId,
            emailConfirmation: email,
            newPassword: data.newPassword,
            currentPassword: data.currentPassword,
        }, osType, osVersion, app, appVersion)
            .then(() => {
            notifyApp('adc-webview:update-password');
        })
            .catch((error) => {
            handleApiRequestError(error, resetForm);
        })
            .finally(() => {
            setSubmitting(false);
        });
    }
    return (React.createElement(MainContainer, null,
        React.createElement(InformationalScreen, { topNavigationProps: {
                title: i18n.t('ChangePassword.title'),
                buttonStart: {
                    element: isLtr ? (React.createElement(IconArrowLeft, { testID: "ChangePassword.back-button" })) : (React.createElement(IconArrowRight, { testID: "ChangePassword.back-button" })),
                    label: i18n.t('ChangePassword.title'),
                    onPress: handleClickBack,
                },
                testID: 'ChangePassword.title',
            }, testID: "ChangePassword" },
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(FormContainer, { hasHiddenUserInput: true },
                        lockoutMessage ? (React.createElement(Banner, { testID: "ChangePassword", message: lockoutMessage, submessage: lockoutSubmessage })) : null,
                        React.createElement(InputPassword, { label: i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.label'), placeholder: i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.placeholder'), isInvalid: errors.currentPassword && touched.currentPassword ? true : false, errorMessage: errors.currentPassword, testID: "currentPassword", onChangeText: handleChange('currentPassword'), onBlur: handleBlur('currentPassword'), value: values.currentPassword, disabled: isLocked }),
                        React.createElement(Button, { variant: "tertiary", label: i18n.t('ChangePassword.content.changePasswordAccountInfoForm.links.forgotPasswordLink.label'), onPress: handleClickForgotPass, testID: "ChangePassword.forgotPassword" }),
                        React.createElement(PasswordInputValidator, { key: `new-password-validator-${formRefreshFlag}`, placeholder: i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.placeholder'), label: i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.label'), errors: errors, touched: touched, handleBlur: handleBlur('newPassword'), password: values.newPassword, onValidate: setMeetsPasswordRequirements, passwordRules: passwordRules, onChangeText: handleChange('newPassword'), testID: "newPassword", disabled: isLocked }),
                        React.createElement(InputPassword, { key: `new-password-check-${formRefreshFlag}`, mt: "$1", label: i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.label'), placeholder: i18n.t('ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.placeholder'), isInvalid: errors.repeatNewPassword && touched.repeatNewPassword ? true : false, errorMessage: errors.repeatNewPassword, testID: "repeatNewPassword", onChangeText: handleChange('repeatNewPassword'), onBlur: handleBlur('repeatNewPassword'), value: values.repeatNewPassword, disabled: isLocked }))),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Button, { label: i18n.t('Global.microcopy.common.save'), disabled: !values.currentPassword ||
                            !values.newPassword ||
                            !values.repeatNewPassword ||
                            !isValid ||
                            isSubmitting ||
                            !meetsPasswordRequirements, onPress: () => handleSubmit(), testID: "ChangePassword.save-button" }))))));
};
export default connector(UpdatePassword);
